import React, { InputHTMLAttributes, ReactNode, forwardRef } from 'react';
import styled from 'styled-components';
import selectOnFocus from '../../../modules/autoselect';

export interface IconTextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'className'> {
    leadingIcon?: ReactNode;
    endAdornment?: ReactNode;
    error?: boolean;
    // Предупреждение без блокировки — оранжевая обводка; error важнее
    warning?: boolean;
    className?: string;
}

const IconTextField = forwardRef<HTMLInputElement, IconTextFieldProps>(
    ({ leadingIcon, endAdornment, error = false, warning = false, disabled, className, type, inputMode, onFocus, onClick, onWheel, ...rest }, ref) => {
        const isNumeric =
            type === 'number' || inputMode === 'numeric' || inputMode === 'decimal';

        const handleClick: React.MouseEventHandler<HTMLInputElement> = (e) => {
            if (isNumeric) selectOnFocus(e);
            onClick?.(e);
        };

        // Колесо над сфокусированным type='number' меняет значение — снимаем фокус,
        // иначе прокрутка страницы правит количество или вес. Только для type='number':
        // у inputMode-вариантов на текстовом инпуте такого поведения нет, и блюр при
        // скролле был бы неожиданным.
        const handleWheel: React.WheelEventHandler<HTMLInputElement> = (e) => {
            if (type === 'number') e.currentTarget.blur();
            onWheel?.(e);
        };

        return (
            <Wrapper
                className={className}
                $disabled={disabled}
                $hasError={error}
                $hasWarning={warning}
                $hasLeading={Boolean(leadingIcon)}
                $hasEnd={Boolean(endAdornment)}
            >
                {leadingIcon && <IconSlot aria-hidden>{leadingIcon}</IconSlot>}
                <Input
                    ref={ref}
                    disabled={disabled}
                    type={type}
                    inputMode={inputMode}
                    onClick={handleClick}
                    onWheel={handleWheel}
                    {...rest}
                />
                {endAdornment && <EndSlot>{endAdornment}</EndSlot>}
            </Wrapper>
        );
    },
);

IconTextField.displayName = 'IconTextField';

export default IconTextField;

const Wrapper = styled.div<{
    $disabled?: boolean;
    $hasError: boolean;
    $hasWarning: boolean;
    $hasLeading: boolean;
    $hasEnd: boolean;
}>`
    width: 100%;
    min-height: 2.875rem;
    box-sizing: border-box;
    display: grid;
    grid-template-columns: ${({ $hasLeading, $hasEnd }) => {
        if ($hasLeading && $hasEnd) return 'auto 1fr auto';
        if ($hasLeading) return 'auto 1fr';
        if ($hasEnd) return '1fr auto';
        return '1fr';
    }};
    align-items: center;
    gap: 0.625rem;
    background: ${({ theme }) => theme.colors.surface};
    border: 1px solid
        ${({ theme, $hasError, $hasWarning }) =>
            $hasError ? theme.colors.danger : $hasWarning ? theme.colors.warning : theme.colors.inputBorder};
    border-radius: 0.875rem;
    padding: 0 0.75rem;
    direction: rtl;
    opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};
    transition:
        border-color ${({ theme }) => theme.motion.fast} ease,
        box-shadow ${({ theme }) => theme.motion.fast} ease;

    &:hover {
        border-color: ${({ theme, $hasError, $hasWarning, $disabled }) =>
            $disabled
                ? undefined
                : $hasError
                  ? theme.colors.danger
                  : $hasWarning
                    ? theme.colors.warning
                    : theme.colors.inputBorderHover};
    }

    &:focus-within {
        border-color: ${({ theme, $hasError, $hasWarning }) =>
            $hasError ? theme.colors.danger : $hasWarning ? theme.colors.warning : theme.colors.ink};
        box-shadow: ${({ theme }) => theme.shadows.focus};
    }

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        min-height: 2.5rem;
        padding: 0 0.625rem;
        gap: 0.5rem;
    }
`;

const IconSlot = styled.span`
    color: ${({ theme }) => theme.colors.icon};
    display: grid;
    place-items: center;
    width: 1.25rem;
    height: 1.25rem;

    svg {
        width: 1.05rem;
        height: 1.05rem;
        flex-shrink: 0;
    }
`;

const EndSlot = styled.span`
    display: inline-flex;
    align-items: center;
    color: ${({ theme }) => theme.colors.icon};
`;

const Input = styled.input`
    border: 0;
    outline: 0;
    background: transparent;
    -webkit-appearance: none;
    appearance: none;
    font-family: ${({ theme }) => theme.fonts.sans};
    font-size: ${({ theme }) => theme.text.lg};
    line-height: 1.2;
    color: ${({ theme }) => theme.colors.ink};
    width: 100%;
    padding: 0.375rem 0;
    direction: rtl;
    text-align: start;
    min-width: 0;

    &::placeholder {
        color: ${({ theme }) => theme.colors.ink4};
    }

    &:disabled {
        color: ${({ theme }) => theme.colors.ink2};
        cursor: not-allowed;
    }

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.textMobile.lg};
    }
`;
